/**
 * Based on SwipeableViews component by phaux@github
 * See https://codesandbox.io/p/sandbox/swipeable-views-2024-pcd2pm
 * and https://github.com/oliviertassinari/react-swipeable-views/issues/676#issuecomment-1933572772
 */

import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import classes from './SwipeableViews.scss';

export type SwipeableViewsProps = {
  index: number;
  onIndexChange: (page: number) => void;
} & React.HTMLProps<HTMLDivElement>;

export default function SwipeableViews({
  className = '',
  index,
  onIndexChange,
  onScroll,
  ...rootProps
}: SwipeableViewsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<number>();
  const lastChildrenCount = useRef(0);

  // on every rerender
  useEffect(() => {
    if (!containerRef.current) return;

    // set aria-hidden and inert on all children that aren't current page
    const currentChild = containerRef.current.children[index];

    for (const child of Array.from(containerRef.current.children)) {
      if (!(child instanceof HTMLElement)) continue;

      child.ariaHidden = child !== currentChild ? 'true' : null;
      child.inert = child !== currentChild;
    }

    // only if number of children changed
    const childrenCount = containerRef.current.children.length;
    if (childrenCount !== lastChildrenCount.current) {
      lastChildrenCount.current = childrenCount;

      // scroll container to the current page instantly
      const pageWidth = containerRef.current.scrollWidth / containerRef.current.children.length;

      containerRef.current?.scrollTo({
        behavior: 'instant',
        left: index * pageWidth,
        top: 0,
      } as unknown as ScrollToOptions); // TODO: remove this hack after update to TS@^5.1 https://github.com/microsoft/TypeScript/issues/47441#issuecomment-1516836705
    }
  });

  // on page index change
  useEffect(() => {
    if (!containerRef.current) return;

    // scroll container to the current page smoothly
    const pageWidth = containerRef.current.scrollWidth / containerRef.current.children.length;

    containerRef.current.scrollTo({
      behavior: 'smooth',
      left: index * pageWidth,
      top: 0,
    });
  }, [index]);

  // on user scroll
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      onScroll?.(event);
      const { currentTarget } = event;

      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      scrollTimeout.current = window.setTimeout(() => {
        // update current page index
        const pageWidth = currentTarget.scrollWidth / currentTarget.children.length;
        const currentPage = Math.round(currentTarget.scrollLeft / pageWidth);
        onIndexChange(currentPage);
      }, 100);
    },
    [onIndexChange, onScroll]
  );

  return (
    <div
      {...rootProps}
      ref={containerRef}
      className={classNames(classes.SwipeableViews, className)}
      onScroll={handleScroll}
    />
  );
}
